/**
 * Pure decision logic for what to do with a photo before it uploads: leave it
 * alone, redraw it smaller, or refuse it outright.
 *
 * Kept import-free on purpose (same pattern as lib/alternates.ts and why) so
 * it runs under `npm run test:handlers`'s --experimental-strip-types Node
 * test runner without dragging in Firebase or any browser-only API. The
 * actual canvas decode/redraw work is browser-only and lives in
 * downscaleImage in lib/offerings.ts, which imports decidePhotoUpload from
 * here rather than duplicating this decision inline.
 *
 * Why this exists: shared/storage.rules caps every class photo at 8 MB
 * (isImageUpload), and a full-resolution iPhone photo routinely exceeds that
 * - refused for every account, Director included, App Check or no App
 * Check. The old shrink step decoded with createImageBitmap only; on any
 * decode failure (some HEIC files, on some browsers) it silently returned
 * the ORIGINAL undecoded file with no size check on that fallback path at
 * all. See lib/offerings.ts's decodeImageSource for the <img> fallback that
 * actually fixes the decode gap; this file only owns what to DO once
 * decodability is known.
 */

/** Firebase Storage rules cap every class photo at 8 MB (shared/storage.rules isImageUpload). */
export const CLASS_PHOTO_MAX_BYTES = 8 * 1024 * 1024;

/** Below this size, an already-compact format is left completely alone. */
export const PHOTO_SKIP_RECOMPRESS_BYTES = 1.5 * 1024 * 1024;

/** Long-edge cap, in pixels, for a downscaled photo. */
export const PHOTO_DOWNSCALE_MAX_DIMENSION = 2000;

/** JPEG quality used when re-encoding a downscaled photo. */
export const PHOTO_DOWNSCALE_JPEG_QUALITY = 0.85;

/**
 * Formats already broadly viewable and reasonably compact on their own -
 * recompressing a small file already in one of these buys nothing.
 */
const COMPACT_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

export interface PhotoUploadInput {
  /** File size in bytes. */
  size: number;
  /** The file's reported MIME type, e.g. "image/heic". */
  type: string;
  /** Whether the browser could decode this file into something drawable. */
  decodable: boolean;
}

export type PhotoUploadDecision =
  | { action: 'skip' }
  | { action: 'downscale' }
  | { action: 'reject'; reason: string };

/**
 * Decide what happens to a photo before it uploads:
 *
 *   - skip:      already small AND already a broadly-compatible format, so
 *                recompressing it would only cost time for no benefit.
 *   - downscale: decodable in this browser - redraw at a capped size and
 *                re-encode as JPEG. The normal path for a full-size phone
 *                photo, iPhone HEIC included, once it decodes at all.
 *   - reject:    could not be decoded at all (odd HEIC edge, ancient
 *                browser) AND is over the Storage rules' hard cap - there is
 *                no safe way to shrink it and no safe way to upload it raw
 *                either, so this fails with a plain-words reason instead of
 *                a doomed upload.
 *
 * Pure and side-effect free: no canvas, no Blob, no File reads. `decodable`
 * is the caller's job to establish first (see decodeImageSource in
 * lib/offerings.ts) since only a real decode attempt can know it.
 */
export function decidePhotoUpload(input: PhotoUploadInput): PhotoUploadDecision {
  const { size, type, decodable } = input;
  if (size < PHOTO_SKIP_RECOMPRESS_BYTES && COMPACT_IMAGE_TYPES.has(type)) {
    return { action: 'skip' };
  }
  if (decodable) {
    return { action: 'downscale' };
  }
  if (size < CLASS_PHOTO_MAX_BYTES) {
    return { action: 'skip' };
  }
  const mb = (size / (1024 * 1024)).toFixed(1);
  return {
    action: 'reject',
    reason: `That photo is too large (${mb} MB). The limit is 8 MB - try a smaller photo or a screenshot of it.`,
  };
}

/**
 * How long one photo may spend being shrunk before the shrink is given up
 * on, counted from when its own turn starts (photoShrinkTurn.ts), not from
 * when it was picked.
 *
 * Why there is a limit at all (2026-10-19, Bobbie, a brand-new iPhone photo
 * in Messages): the Netlify logs showed her phone never asked to reserve the
 * upload, so the wait was on the phone, before the network. The shrink is the
 * only long step there, and nothing bounded it: a decode or a canvas encode
 * that never calls back left "Sending 0%" on screen for as long as she kept
 * looking. A healthy 12 megapixel decode and re-encode on a phone takes a
 * second or two, so eight seconds is a stuck shrink, not a slow one.
 */
export const PHOTO_SHRINK_DEADLINE_MS = 8_000;

/**
 * The Worker's share of that deadline (refute round 1 on #1401): loading the
 * worker chunk, starting it and decoding. Past it, a worker that has
 * finished its full-size decode keeps going alone (refute round 2 on #1401);
 * one that has not is ended, and only then does the main thread pipeline,
 * with the <img> fallback that handles HEIC on iPhone, start. Never two
 * full-size decodes at once (refute round 3 on #1401; photoShrink.ts
 * shrinkWithinBudget). The overall deadline stays fixed rather than
 * growing with file size: every photo here is at most a few tens of
 * megapixels, a healthy phone decodes and re-encodes one in a second or
 * two whatever its byte size, and a limit that grows with size is a limit
 * a person can sit through for longer, which is the exact failure this
 * exists to end.
 */
export const PHOTO_SHRINK_WORKER_CAP_MS = 4_000;

/** What the upload gateway accepts as an image as is (attachmentUpload.mjs ALLOWED_IMAGE_TYPES, minus GIF, which never shrinks). */
const SEND_AS_IS_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

export const PHOTO_SHRINK_TIMEOUT_REASON =
  'That photo took too long to get ready on this phone, so it was not sent. Try it again. If it keeps happening, take a screenshot of the photo and send the screenshot.';

export type PhotoShrinkTimeoutDecision =
  | { action: 'original' }
  | { action: 'reject'; reason: string };

/**
 * The shrink ran out of time. Send the photo exactly as it was picked when
 * the server takes that type and size; otherwise say so in plain words. A
 * HEIC original is never sent raw: the gateway refuses it, and it would show
 * as a blank box on every phone that is not an Apple one.
 *
 * Pure: `type` is the inferred MIME type, `size` the original's bytes.
 */
export function decidePhotoAfterShrinkTimeout(input: { size: number; type: string }): PhotoShrinkTimeoutDecision {
  const type = (input.type || '').toLowerCase();
  if (SEND_AS_IS_TYPES.has(type) && input.size > 0 && input.size < CLASS_PHOTO_MAX_BYTES) {
    return { action: 'original' };
  }
  return { action: 'reject', reason: PHOTO_SHRINK_TIMEOUT_REASON };
}
